import { Fragment } from "react";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import has from "lodash/has";
import find from "lodash/find";
import { getStore } from "@store";
import sypiAdapter from "@features/sypi/adapters";
import {
  isEmptyObject,
  isPlainObject,
} from "@utils/object-helpers";
import { getDateDifference } from "@utils/time-helpers";
import {
  mergeSypiVersionChanges,
  mergeLoggedChanges,
  currentKeysOccurrenceChangesReport,
} from "@features/sypi/_utils/sypi-interface-helpers";
import { logDiffs } from "@features/sypi/_utils/sypi-ui-helpers";
import { CONFIG_SCHEDULER, SYPI_S3 } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  showSpinner,
  stopSpinner,
  reportError,
  clearChanges,
  setAlertDock,
  clickTab,
} from "@actions/global";
import {
  loadScheduledDraftSet,
  loadHistorySet,
  setScheduledHistoryView,
  loadSummaryChangesReview,
  loadScheduledDraft,
  toggleSummaryConfirm,
  toggleSummaryModal,
  resetSypiConfig,
} from "@features/sypi/actions";
import { fetchSypiConfig, fetchSypiSchema } from "./sypi-config";
import { dispatchErrors, sendFinalReport } from "./sypi-errors";

/**
 * SyPI history / scheduled-list retrieval + revert flows.
 *
 * `fetchScheduledDraftHistoryList` populates the scheduled-drafts or the
 * completed-history tab; `loadHistoryVersion` builds a revert bundle for
 * a completed change; `fetchNonActiveConfigDetails` + `checkForNonActiveConfigErrors`
 * validate applying an active-config change to another release version.
 */

export const fetchScheduledDraftHistoryList = async (
  release_version,
  fetchScheduledDraft = true,
  currentPage = 1,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let scheduleDraftList = fetchScheduledDraft
    ? await sypiAdapter.fetchScheduledDraftList()
    : await sypiAdapter.fetchHistoryConfig({
        limit: 25,
        offset: currentPage - 1,
      });
  let { apiError, configSchedules, offset, total_pages } = scheduleDraftList;
  if (apiError && apiError.details) {
    store.dispatch(
      reportError("Unable to load the scheduled and drafts sypi configurations")
    );
  } else {
    if (fetchScheduledDraft) {
      store.dispatch(loadScheduledDraftSet(configSchedules));
    } else {
      let historySet = configSchedules.map(
        (configSchedule) =>
          (configSchedule = {
            ...configSchedule,
            revertable: !(
              getDateDifference(configSchedule.liveScheduleDate) > 30
            ),
          })
      );
      store.dispatch(loadHistorySet(historySet, total_pages, offset));
    }
  }
  store.dispatch(stopSpinner());
};

export const fetchScheduledDraftConfig = async (paramConfig, isScheduled) =>
  isScheduled
    ? await sypiAdapter.fetchScheduledConfig(paramConfig)
    : await sypiAdapter.fetchDraftConfig(paramConfig);

export const loadHistoryConfig = async (historyConfig, store = getStore()) => {
  store.dispatch(showSpinner());
  const storeContents = store.getState();
  const { historySet } = storeContents.sypiConfigScheduler;
  const scheduledHistoryConfig = find(
    historySet,
    (historyCF) =>
      historyCF.liveScheduleDate === historyConfig.liveScheduleDate &&
      historyCF.release_version === historyConfig.release_version
  );
  if (scheduledHistoryConfig && !isEmptyObject(scheduledHistoryConfig)) {
    store.dispatch(setScheduledHistoryView(scheduledHistoryConfig));
  }
  store.dispatch(stopSpinner());
};

export const loadScheduledDraftConfig = async (
  sypiVersionsPayload = null,
  importedConfig = null,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const { sypiNewView } = store.getState();
  const { changeDict, initialConfig, importedChanges } = sypiNewView;
  const summaryView = sypiVersionsPayload?.summaryView;

  let inputSourceKeysReport = {};
  if (
    changeDict &&
    !isEmptyObject(changeDict) &&
    importedChanges &&
    !isEmptyObject(importedChanges)
  ) {
    Object.keys(importedChanges).forEach((key) => {
      inputSourceKeysReport[key] = {
        imported:
          changeDict[key] === importedChanges[key] ||
          (!changeDict[key] && !importedChanges[key]),
      };
    });
  }

  let configToChange = importedConfig ? importedConfig.config : initialConfig;

  let summaryReviewBundle =
    summaryView && summaryView.length
      ? [...summaryView]
      : [
          ...(changeDict && !isEmptyObject(changeDict)
            ? mergeSypiVersionChanges(
                changeDict,
                SYPI_S3,
                configToChange,
                inputSourceKeysReport
              )
            : []),
        ];
  store.dispatch(
    loadSummaryChangesReview(summaryReviewBundle, importedConfig || null)
  );
  store.dispatch(stopSpinner());
};

export const fetchNonActiveConfigDetails = async (
  version,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const changesToCopy = store.getState().sypiNewView?.changeDict;
  let config =
    store.getState().sypiConfigScheduler?.otherConfigsScheduledDrafts?.[
      version
    ];
  let confirmedReport = {};

  if (!config) {
    config = await fetchSypiConfig(version);
    const { sypi_config } = config;
    confirmedReport = await checkForNonActiveConfigErrors(
      version,
      sypi_config,
      changesToCopy
    );

    if (confirmedReport.type !== "ALERT_DOCK") {
      await loadScheduledDraftConfig(null, {
        config: sypi_config,
        release_version: version,
      });
    }
  }

  if (confirmedReport.type) {
    sendFinalReport(confirmedReport);
  }
  const success = confirmedReport.type !== "ALERT_DOCK";
  store.dispatch(stopSpinner());
  return success;
};

export const checkForNonActiveConfigErrors = async (
  release_version,
  non_active_config,
  changes_to_copy,
  store = getStore()
) => {
  // CHECK FOR CONFIG NOT CREATED
  if (typeof non_active_config !== "object") {
    return {
      type: "ALERT_DOCK",
      title: "Unable to load config.",
      message: `Configuration File for release version ${release_version} has not been created yet. Visit the Import page to create a new Configuration File by importing values from another client or release version, or generate a blank Configuration File using schema default values. \n`,
    };
  }

  // CHECK FOR EXISTING SCHEMA SYNC ERROR
  const { formattedSchema } = await fetchSypiSchema(release_version);
  const {
    fullErrorReport: { schemaErrors },
  } = dispatchErrors(non_active_config, formattedSchema, release_version);
  if (schemaErrors && schemaErrors?.length) {
    return {
      type: "ALERT_DOCK",
      title: "Schema Config Sync Error",
      message: (
        <Fragment>
          The following errors in the configuration file {release_version} have
          a conflict with schema validation: <br />
          {Object.values(schemaErrors).map((error) => (
            <b key={error}>
              {`${error}.`} <br />
            </b>
          ))}
          Please contact the application development team for assistance.
        </Fragment>
      ),
    };
  }

  // CHECK FOR SCHEMA SYNC ERROR IF NEW CHANGE IS APPLIED
  const invalid_properties = [];
  Object.keys(changes_to_copy).forEach((change) => {
    let existingValue = get(non_active_config, change);
    let copiedValue = get(changes_to_copy, change);
    if (
      !has(non_active_config, change) ||
      (typeof existingValue !== typeof copiedValue &&
        isPlainObject(existingValue) !== isPlainObject(copiedValue))
    ) {
      invalid_properties.push(change);
    }
  });
  if (invalid_properties && invalid_properties.length) {
    return {
      type: "ALERT_DOCK",
      title: "Schema Config Sync Error",
      message: (
        <Fragment>
          The following properties are not found in the configuration file{" "}
          {release_version} and copying these changes will cause a conflict with
          schema validation:
          <br />
          {invalid_properties.map((key) => (
            <b key={key}>
              {`${key}`}
              <br />
            </b>
          ))}
          Please contact the application development team for assistance.
        </Fragment>
      ),
    };
  }

  // CHECK FOR MULTISCHEDULER ERROR
  const { scheduleDraftSet } = store.getState().sypiConfigScheduler;
  const { configSchedules } =
    scheduleDraftSet && scheduleDraftSet.length
      ? { configSchedules: scheduleDraftSet }
      : await sypiAdapter.fetchScheduledDraftList();
  const scheduledChanges = configSchedules?.filter(
    (configSchedule) =>
      configSchedule.status === "scheduled" &&
      configSchedule.release_version === release_version
  );
  let { scheduledChangesWithCommonKeys } = currentKeysOccurrenceChangesReport(
    changes_to_copy,
    scheduledChanges
  );
  if (!isEmptyObject(scheduledChangesWithCommonKeys)) {
    return {
      type: "ALERT_DOCK",
      title: "Multischeduler Error",
      message: (
        <Fragment>
          The following keys already have 3 or more changes scheduled in
          configuration file {release_version}:
          <br />
          {Object.keys(scheduledChangesWithCommonKeys).map((key) => (
            <b key={key}>
              {`${key}.`} <br />
            </b>
          ))}
        </Fragment>
      ),
    };
  }

  return {
    type: "TOAST_MESSAGE",
    message: "Summary of changes is ready to review",
  };
};

export const loadHistoryVersion = async (history, store = getStore()) => {
  store.dispatch(showSpinner());
  store.dispatch(
    clickTab(STATE_CONTANTS.DISPLAY_REVERT_SUMMARY, CONFIG_SCHEDULER)
  );
  const {
    history_path,
    release_version,
    platform_9,
    summaryView,
    sectionsModified,
  } = history;
  const { scheduleDraftSet, scheduleDraft } =
    store.getState().sypiConfigScheduler;
  const sypiConfig = await fetchSypiConfig(release_version);
  const currentConfig = sypiConfig.sypi_config;
  if (platform_9 === null) {
    store.dispatch(
      setAlertDock({
        title: "Version ID not found",
        message:
          "Revert was unsuccessful. Version ID was not found. Please contact the Platform Team for further assistance.",
      })
    );
  } else if (typeof currentConfig === "string") {
    store.dispatch(
      reportError(
        `Configuration File for release version ${release_version} has not been created yet. Visit the Import page to create a new Configuration File by importing values from another client or release version, or generate a blank Configuration File using schema default values. \n`
      )
    );
  } else {
    //for every change, replace the value of key name to oldvaluename
    let updatedConfig = cloneDeep(currentConfig);
    let changeDict = {};
    summaryView.map(
      (change) =>
        (changeDict[change.keyName] =
          change.section === "Features" && change.oldKeyValue === ""
            ? false
            : change.oldKeyValue)
    );
    updatedConfig = mergeLoggedChanges(updatedConfig, changeDict);
    const sypiS3 = logDiffs(currentConfig, updatedConfig);
    const sypiConfigs = {
      sypiS3: { ...updatedConfig },
    };
    let summaryReviewBundle = mergeSypiVersionChanges(
      sypiS3,
      SYPI_S3,
      currentConfig
    );
    //multischeduler errors
    let scheduledChanges = scheduleDraftSet?.filter(
      (configSchedule) =>
        configSchedule.status === "scheduled" &&
        configSchedule.release_version === release_version
    );

    let { scheduledChangesWithCommonKeys, successFailedKeysReport } =
      currentKeysOccurrenceChangesReport(sypiS3, scheduledChanges);
    if (!isEmptyObject(scheduledChangesWithCommonKeys)) {
      store.dispatch(
        clickTab(STATE_CONTANTS.GENERAL_SCHEDULER_ERROR, CONFIG_SCHEDULER)
      );
      store.dispatch(toggleSummaryConfirm(true));
      let multischedulerError = Object.keys(successFailedKeysReport).filter(
        (key) => successFailedKeysReport[key].disable === true
      );
      summaryReviewBundle = summaryReviewBundle.map((config) =>
        multischedulerError.includes(config.keyName)
          ? { ...config, disable: true }
          : config
      );
    }
    store.dispatch(resetSypiConfig());
    store.dispatch(
      loadScheduledDraft({
        ...scheduleDraft,
        history_path,
        status: "scheduled",
        schemaPath: { sypiS3 },
        sectionsModified,
        release_version,
        sypiConfigs,
        summaryView: summaryReviewBundle,
      })
    );
    if (summaryReviewBundle.length) {
      store.dispatch(toggleSummaryModal(true));
    } else {
      store.dispatch(
        clickTab(STATE_CONTANTS.COMPLETED_STATUS, CONFIG_SCHEDULER)
      );
      store.dispatch(clearChanges());
      store.dispatch(
        setAlertDock({
          title: "There are no changes to be made",
          message: "The current config already contains these changes.",
        })
      );
    }
  }
  store.dispatch(stopSpinner());
};

export const scheduleRevert = async (store = getStore()) => {
  const { scheduleDraft } = store.getState().sypiConfigScheduler;
  let { release_version, history_path } = scheduleDraft;
  let report = {};
  let configSchedules = (({ release_version, history_path, ...config }) =>
    config)(scheduleDraft);
  let result = await sypiAdapter.scheduleRevertConfig({
    configSchedules,
    release_version,
    history_path,
  });
  if (
    result &&
    (result.configSchedules ||
      (Array.isArray(result)
        ? result[0][1]?.success && result[1][1]?.success
        : false))
  ) {
    report = {
      type: "TOAST_MESSAGE",
      message: `Saving the config was successful.`,
    };
    store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  } else {
    report = {
      type: "ALERT_DOCK",
      title: "Failed to revert.",
      message: result,
    };
    store.dispatch(clickTab(STATE_CONTANTS.COMPLETED_STATUS, CONFIG_SCHEDULER));
  }
  store.dispatch(clearChanges());
  return report;
};
